import { EstimateStatus } from "@rebuild/shared";
import type { EstimationFilters } from "@/interfaces/estimation-filters";
import type { Estimation } from "@/interfaces/estimation.interface";
import { compareValues } from "@/pages/dashboard/utils/sort";
import { userSelector } from "@/redux/selectors/user.selector";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { CollectionReference } from "firebase/firestore";
import { collection, onSnapshot } from "firebase/firestore";
import { appSelector } from "@/redux/selectors/app.selector";
import { useFirestore } from "@/context/firebase-context";

export const useEstimations = () => {
  const users = useSelector(userSelector.users);

  const organizationId = useSelector(appSelector.organizationId);

  const currentDataLength = useRef(0);
  const hasFetchedData = useRef(false);
  const estimationFilters = useRef<EstimationFilters>({});
  const [isLoading, setLoading] = useState<boolean>(true);
  const [estimations, setEstimations] = useState<Estimation[]>([]);

  const firestore = useFirestore();

  useEffect(() => {
    if (!organizationId) return;

    const estimationsCollectionRef = collection(firestore, `/organizations/${organizationId}/estimates`) as CollectionReference<Estimation>;

    const unsubscribe = onSnapshot(estimationsCollectionRef, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as Estimation[];

      if (!hasFetchedData.current) {
        setEstimations(data);
        hasFetchedData.current = true;
        setLoading(false);
      } else if (data.length !== currentDataLength.current) {
        setEstimations(data);
        currentDataLength.current = data.length;
      }
    });

    return () => unsubscribe();
  }, [firestore, organizationId]);

  const sort = useCallback((estimation1: Estimation, estimation2: Estimation) => {
    const dateA = new Date(estimation1.dateCreated);
    const dateB = new Date(estimation2.dateCreated);

    const timeA = dateA.getTime();
    const timeB = dateB.getTime();

    if (isNaN(timeA) && isNaN(timeB)) return 0;
    if (isNaN(timeA)) return 1;
    if (isNaN(timeB)) return -1;

    if (dateA.getTime() === dateB.getTime()) {
      const timestampA = estimation1.timestampCreated || 0;
      const timestampB = estimation2.timestampCreated || 0;
      return timestampB - timestampA;
    }

    return timeB - timeA;
  }, []);

  const getFilteredEstimations = useCallback(
    (page: number, pageSize: number, filters?: EstimationFilters) => {
      if (filters) {
        estimationFilters.current = {
          ...estimationFilters.current,
          ...filters,
        };
      }

      let filteredEstimations = estimations ? structuredClone(estimations) : [];

      const { userId: userIdFilter, projectName, status, row, order } = estimationFilters.current ?? {};

      if (userIdFilter) {
        filteredEstimations = filteredEstimations?.filter((estimation) =>
          userIdFilter === "Unassigned"
            ? estimation.assignee === "" || estimation.assignee === "Unassigned"
            : estimation.assignee === userIdFilter,
        );
      }

      if (projectName) {
        filteredEstimations = filteredEstimations?.filter(
          (estimation) =>
            estimation.projectName?.toLowerCase()?.trim().includes(projectName.toLowerCase().trim()) ||
            estimation.estimateReferenceNumber?.toLowerCase()?.trim().includes(projectName.toLowerCase().trim()) ||
            estimation.propertyAddress?.toLowerCase()?.trim().includes(projectName.toLowerCase().trim()),
        );
      }

      if (status && status.length > 0 && !status.includes(EstimateStatus.ALL)) {
        filteredEstimations = filteredEstimations?.filter((estimation) => {
          if (estimation.status === EstimateStatus.NO_STATUS) {
            estimation.status = EstimateStatus.RECEIVED;
          }
          return status.includes(estimation.status);
        });
      }

      filteredEstimations = filteredEstimations?.sort(sort) || [];

      if (row && order) {
        const userMap = new Map(users?.map((user) => [user.lassoUserId, user]));

        switch (row) {
          case "dateCreated":
            filteredEstimations = [...filteredEstimations].sort((a, b) => {
              const dateA = new Date(a.dateCreated);
              const dateB = new Date(b.dateCreated);

              const timeA = dateA.getTime();
              const timeB = dateB.getTime();

              if (isNaN(timeA) && isNaN(timeB)) return 0;
              if (isNaN(timeA)) return order === "ascended" ? 1 : 1;
              if (isNaN(timeB)) return order === "ascended" ? -1 : -1;

              // If dates are the same, use timestampCreated as fallback
              if (dateA.getTime() === dateB.getTime()) {
                const timestampA = a.timestampCreated || 0;
                const timestampB = b.timestampCreated || 0;
                return order === "ascended" ? timestampA - timestampB : timestampB - timestampA;
              }

              return order === "ascended" ? timeA - timeB : timeB - timeA;
            });
            break;

          case "assignee":
            filteredEstimations = [...filteredEstimations].sort((a, b) => {
              const userA = userMap.get(a.assignee);
              const userB = userMap.get(b.assignee);

              const nameA = userA?.firstName?.toLowerCase() ?? null;
              const nameB = userB?.firstName?.toLowerCase() ?? null;

              if (nameA === null && nameB === null) return 0;
              if (nameA === null) return order === "ascended" ? 1 : -1;
              if (nameB === null) return order === "ascended" ? -1 : 1;

              return order === "ascended" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            });
            break;

          default:
            filteredEstimations = [...filteredEstimations].sort((a, b) => compareValues(a[row], b[row], order));
            break;
        }
      }

      const startIndex = page * pageSize;

      return {
        data: filteredEstimations?.slice(startIndex, startIndex + pageSize),
        totalItems: filteredEstimations?.length,
      };
    },
    [estimations, sort, users],
  );

  const getAllEstimations = useCallback((): Estimation[] => {
    return estimations.sort(sort);
  }, [estimations, sort]);

  return {
    isLoading,
    getAllEstimations,
    getFilteredEstimations,
  };
};
